import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetCustomer } from 'api/customer';
import { useAuth } from 'auth';
import {
    ActivityStatusResponse,
    getBakerEnv,
    getMockActivityStatus,
    getBakerApiBaseUrl,
    getMockAthletesStatus,
    AthleteStatusResponse,
    mergeActivityStatuses,
    PagedActivityBakeStatusResponse,
} from 'processing-status/activity-status-util';
import { useEffect, useRef, useState } from 'react';

// Baker API swagger docs: https://sturdy-pancake-3e913kw.pages.github.io/openapi.html

// simply turn s to ms
const pollRate = (s: number) => s * 1000;

const env = getBakerEnv();

async function getBulkActivityStatus(
    token: string,
    region: string,
    activityIds: string[],
    customerId?: string,
    mockMode?: boolean,
) {
    const now = new Date();
    const twoDaysAgo = new Date(new Date().setDate(now.getDate() - 2));

    const path = `/api/activity/bakestatus?StartTime=${twoDaysAgo.toISOString()}&EndTime=${now.toISOString()}`;
    const url = getBakerApiBaseUrl(region, env) + path;

    // for local development
    if (mockMode && activityIds) {
        const response = [];

        for (let i = 0; i < activityIds.length; i++) {
            response.push(getMockActivityStatus(activityIds[i], i));
        }

        return response;
    }

    try {
        const req = await fetch(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                // optional if using Bearer token (id extracted from token)
                ...(customerId ? { CustomerId: customerId } : {}),
            },
        });

        if (!req.ok) {
            throw new Error('Error calling the Baker API (bakestatus)');
        }

        const res = await req.json();
        return res;
    } catch (error) {
        return [];
    }
}

export const useGetActivityStatus = (activityIds?: string[], mockMode?: boolean) => {
    const user = useGetCustomer();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const prevStatusRef = useRef<ActivityStatusResponse[] | undefined>(undefined);

    const {
        data: statuses,
        error,
        isLoading,
    } = useQuery({
        queryKey: ['activity_status'],
        queryFn: async () => {
            const token = mockMode ? 'no token' : await auth.getAccessToken();

            if (!token || !user.data || !activityIds) {
                return [];
            }

            return getBulkActivityStatus(token, user.data?.aws_region, activityIds, user.data?.id + '', mockMode);
        },
        refetchInterval: pollRate(10),
    });

    /**
     *  check whether we need to invalidate an activity due to a status update
     */
    useEffect(() => {
        if (!statuses || !prevStatusRef.current) {
            prevStatusRef.current = statuses; // initialize first time
            return;
        }

        statuses.forEach((s: ActivityStatusResponse) => {
            const prev = prevStatusRef.current?.find((p) => p.activityId === s.activityId);

            const isNowBaked = s.status?.includes('baked');
            const wasBaked = prev?.status?.includes('baked');

            if (isNowBaked && !wasBaked) {
                queryClient.invalidateQueries({
                    queryKey: ['activities', s.activityId],
                });
                queryClient.invalidateQueries({
                    queryKey: ['activities', 'deep', s.activityId],
                });
            }
        });

        // update snapshot for next poll
        prevStatusRef.current = statuses;
    }, [statuses, queryClient]);

    return { statuses, error, isLoading };
};

const PAGE_SIZE = 10;

async function getPaginatedActivityBakeStatus(
    token: string,
    region: string,
    pageNumber: number,
    customerId?: string,
    mockMode?: boolean,
    mockActivityIds?: string[],
): Promise<PagedActivityBakeStatusResponse> {
    const now = new Date();
    const twoDaysAgo = new Date(new Date().setDate(now.getDate() - 2));

    const params = new URLSearchParams({
        startTime: twoDaysAgo.toISOString(),
        endTime: now.toISOString(),
        pageNumber: String(pageNumber),
        pageSize: String(PAGE_SIZE),
    });

    const url = getBakerApiBaseUrl(region, env) + `/api/v2/Activity/BakeStatus?${params.toString()}`;

    if (mockMode && mockActivityIds) {
        // simulate real network latency for pages after the first, so the loading spinner may be visible when
        // testing locally - the real endpoint's fetch(...) below naturally takes some time, but this branch was
        // resolving synchronously, making the loading state flip too fast for a browser paint to ever show it
        if (pageNumber > 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
        }

        const allItems = mockActivityIds.map(getMockActivityStatus);
        const totalPages = Math.ceil(allItems.length / PAGE_SIZE);
        const start = (pageNumber - 1) * PAGE_SIZE;
        const items = allItems.slice(start, start + PAGE_SIZE);
        return { items, pageNumber, pageSize: PAGE_SIZE, totalCount: allItems.length, totalPages };
    }

    try {
        const req = await fetch(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                ...(customerId ? { CustomerId: customerId } : {}),
            },
        });

        if (!req.ok) {
            throw new Error('Error calling the Baker API (bakestatus paginated)');
        }

        return await req.json();
    } catch (error) {
        return { items: [], pageNumber, pageSize: PAGE_SIZE, totalCount: 0, totalPages: 0 };
    }
}

/**
 * Fetches bake status for activities in a time range, paginated.
 * Uses GET /api/v2/Activity/BakeStatus
 */
export const useGetPaginatedActivityStatus = (mockMode?: boolean, mockActivityIds?: string[]) => {
    const user = useGetCustomer();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const prevStatusRef = useRef<ActivityStatusResponse[] | undefined>(undefined);
    const statusMapRef = useRef<Map<string, ActivityStatusResponse>>(new Map());
    const [allStatuses, setAllStatuses] = useState<ActivityStatusResponse[]>([]);

    const query = useInfiniteQuery({
        queryKey: ['activity_status_paged'],
        queryFn: async ({ pageParam }) => {
            const token = mockMode ? 'no token' : await auth.getAccessToken();
            if (!token || !user.data) {
                return { items: [], pageNumber: pageParam, pageSize: PAGE_SIZE, totalCount: 0, totalPages: 0 };
            }
            return getPaginatedActivityBakeStatus(
                token,
                user.data.aws_region,
                pageParam,
                user.data.id + '',
                mockMode,
                mockActivityIds,
            );
        },
        initialPageParam: 1,
        // totalPages isn't in the OpenAPI required list, so fall back to deriving it from totalCount/pageSize
        getNextPageParam: (lastPage) => {
            const totalPages = lastPage.totalPages ?? Math.ceil(lastPage.totalCount / lastPage.pageSize);
            return lastPage.pageNumber < totalPages ? lastPage.pageNumber + 1 : undefined;
        },
        // slows down once several pages are loaded to mitigate polling load as scroll depth increases
        refetchInterval: (activeQuery) => {
            const pageCount = activeQuery.state.data?.pages.length ?? 0;
            return pageCount > 3 ? pollRate(20) : pollRate(10);
        },
        enabled: !!user.data,
    });

    useEffect(() => {
        const freshItems = query.data?.pages.flatMap((p) => p.items) ?? [];
        if (!freshItems.length) {
            return;
        }

        // upsert instead of replacing: with offset pagination, an activity can shift off an already-fetched page
        // between polls (e.g. once resorted by a newer modifiedDate elsewhere), so we keep the last-seen entry for
        // every activity rather than letting it disappear just because this poll's pages no longer include it
        statusMapRef.current = mergeActivityStatuses(statusMapRef.current, freshItems);

        setAllStatuses(Array.from(statusMapRef.current.values()));
    }, [query.data]);

    useEffect(() => {
        if (!allStatuses.length || !prevStatusRef.current) {
            prevStatusRef.current = allStatuses;
            return;
        }

        allStatuses.forEach((s) => {
            const prev = prevStatusRef.current?.find((p) => p.activityId === s.activityId);
            const isNowBaked = s.status?.includes('baked');
            const wasBaked = prev?.status?.includes('baked');

            if (isNowBaked && !wasBaked) {
                queryClient.invalidateQueries({ queryKey: ['activities', s.activityId] });
                queryClient.invalidateQueries({ queryKey: ['activities', 'deep', s.activityId] });
            }
        });

        prevStatusRef.current = allStatuses;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(allStatuses), queryClient]);

    return {
        statuses: allStatuses,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading,
        error: query.error,
    };
};

export interface BakeStatusTimeWindow {
    statusChangedAtStart: string;
    statusChangedAtEnd: string;
}

async function getActivityAthleteBakeStatus(
    token: string,
    region: string,
    athleteIds: string[],
    customerId?: string,
    timeWindow?: BakeStatusTimeWindow,
) {
    const params = new URLSearchParams();
    athleteIds.forEach((id) => params.append('athleteIds', id));
    if (timeWindow) {
        params.append('statusChangedAtStart', timeWindow.statusChangedAtStart);
        params.append('statusChangedAtEnd', timeWindow.statusChangedAtEnd);
    }
    const path = `/api/ActivityAthlete/BakeStatus?${params.toString()}`;
    const url = getBakerApiBaseUrl(region, env) + path;

    try {
        const req = await fetch(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                ...(customerId ? { CustomerId: customerId } : {}),
            },
        });

        if (!req.ok) {
            throw new Error('Error calling the Baker API (ActivityAthlete/BakeStatus)');
        }

        const res: AthleteStatusResponse[] = await req.json();
        return res;
    } catch (error) {
        return [];
    }
}

/**
 * Fetches bake status for a list of athlete IDs across all activities.
 * Uses GET /api/ActivityAthlete/BakeStatus
 */
export const useGetActivityAthleteBakeStatus = (athleteIds?: string[], timeWindow?: BakeStatusTimeWindow) => {
    const user = useGetCustomer();
    const auth = useAuth();

    const isEnabled = !!user.data && !!athleteIds && athleteIds.length > 0;

    return useQuery({
        queryKey: ['activity_athlete_bake_status', athleteIds, timeWindow],
        queryFn: async () => {
            const token = await auth.getAccessToken();

            if (!token || !user.data || !athleteIds) {
                return [];
            }

            return getActivityAthleteBakeStatus(token, user.data.aws_region, athleteIds, user.data.id + '', timeWindow);
        },
        enabled: isEnabled,
        refetchInterval: () => {
            if (timeWindow && new Date(timeWindow.statusChangedAtEnd).getTime() < Date.now()) {
                return false;
            }
            return pollRate(10);
        },
    });
};

async function getActivityAthletesStatus(
    token: string,
    region: string,
    activityId: string,
    customerId?: string,
    mockMode?: boolean,
) {
    const path = `/api/activity/${activityId}/ActivityAthleteBakeStatus`;
    const url = getBakerApiBaseUrl(region, env) + path;

    // for local development
    if (mockMode && activityId) {
        const response = getMockAthletesStatus(activityId);
        return response;
    }

    try {
        const req = await fetch(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                // optional if using Bearer token (id extracted from token)
                ...(customerId ? { CustomerId: customerId } : {}),
            },
        });

        if (!req.ok) {
            throw new Error(`Error calling the Baker API (ActivityAthleteBakeStatus) for activity: ${activityId}`);
        }

        const res: AthleteStatusResponse[] = await req.json();
        return res;
    } catch (error) {
        return undefined;
    }
}

/**
 * Fetches the athletes' status for a single activity.
 * Synced to activity status polling via query invalidation.
 * Refetches conditionally to prevent unnecessary requests.
 */
export const useGetActivityAthletesStatus = (
    activityId: string,
    enabled = true,
    needsRefetch: boolean,
    mockMode?: boolean,
) => {
    const user = useGetCustomer();
    const auth = useAuth();

    const isQueryEnabled = enabled && !!user.data;

    const activityAthletesStatus = useQuery({
        queryKey: ['activity_athletes_status', activityId],
        queryFn: async () => {
            const token = mockMode ? 'no token' : await auth.getAccessToken();

            if (!token || !user.data) {
                return undefined;
            }

            return getActivityAthletesStatus(token, user.data?.aws_region, activityId, user.data?.id + '', mockMode);
        },
        enabled: isQueryEnabled,
        staleTime: 0,
        refetchOnWindowFocus: false,
        // Matches the activity status poll. An athlete finishing should change the activity's
        // own status string, which that poll picks up and which refreshes this query in turn.
        refetchInterval: needsRefetch ? pollRate(10) : false,
    });
    return activityAthletesStatus;
};
